/**
 * Cookie consent storage and helpers for GDPR-compliant cookie management.
 * Consent is stored in localStorage; non-essential scripts should only load when consented.
 */

export const COOKIE_CONSENT_KEY = 'wsss_cookie_consent';

export interface CookieConsent {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  updatedAt: string; // ISO date
}

export const getDefaultConsent = (): CookieConsent => ({
  essential: true,
  analytics: false,
  marketing: false,
  updatedAt: new Date().toISOString(),
});

/**
 * Returns the current consent from localStorage, or null if not set (no consent yet).
 */
export function getCookieConsent(): CookieConsent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    if (typeof parsed.essential !== 'boolean') return null;
    return {
      essential: true, // always true
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      updatedAt: parsed.updatedAt || new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

/**
 * Saves consent to localStorage.
 */
export function setCookieConsent(consent: CookieConsent): void {
  if (typeof window === 'undefined') return;
  const toStore: CookieConsent = {
    ...consent,
    essential: true,
    updatedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(toStore));
  } catch (e) {
    console.warn('Failed to save cookie consent', e);
  }
}

/**
 * Whether the user has given consent (any stored value means they made a choice).
 */
export function hasCookieConsentChoice(): boolean {
  return getCookieConsent() !== null;
}

/**
 * Use before loading analytics scripts (e.g. Google Analytics).
 * Returns true only if the user has consented to analytics.
 */
export function shouldLoadAnalytics(): boolean {
  const consent = getCookieConsent();
  return consent !== null && consent.analytics === true;
}

/**
 * Use before loading marketing/tracking scripts.
 * Returns true only if the user has consented to marketing cookies.
 */
export function shouldLoadMarketing(): boolean {
  const consent = getCookieConsent();
  return consent !== null && consent.marketing === true;
}

/**
 * Load a script by URL only if the given consent check passes.
 */
export function loadScriptIfConsented(
  src: string,
  consentCheck: () => boolean,
  options?: { id?: string; async?: boolean }
): void {
  if (typeof document === 'undefined' || !consentCheck()) return;
  const existing = options?.id ? document.getElementById(options.id) : null;
  if (existing) return;
  const script = document.createElement('script');
  script.src = src;
  if (options?.id) script.id = options.id;
  if (options?.async !== false) script.async = true;
  document.head.appendChild(script);
}
